import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Sparkles, Eye, Layers, Cpu, ArrowRight,
  Globe, Star, Zap, Shield, Award, ChevronRight
} from "lucide-react";

/* ── language switcher pills ── */
const LANGS = [
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
  { code: "ar", label: "ع" },
];

/* ── section icons ── */
const OFFER_ICONS = ["✦", "⬡", "◈"];

export default function AboutPage() {
  const { t, i18n } = useTranslation();
  const [activeLang, setActiveLang] = useState(i18n.language?.slice(0, 2) || "en");

  const switchLang = (code) => {
    setActiveLang(code);
    i18n.changeLanguage(code);
  };

  /* ── safe array read – fixes "steps.map is not a function" ── */
  const rawSteps   = t("about.how.steps",   { returnObjects: true });
  const rawWhy     = t("about.why.items",   { returnObjects: true });
  const steps      = Array.isArray(rawSteps)  ? rawSteps  : [];
  const whyItems   = Array.isArray(rawWhy)    ? rawWhy    : [];

  const isRtl = activeLang === "ar";

  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      style={{
        minHeight: "100vh",
        background: "#060d0a",
        overflowX: "hidden",
        fontFamily: "'Cormorant Garamond','Georgia',serif",
        position: "relative",
      }}
    >
      <style>{CSS}</style>

      {/* ── Animated grid background ── */}
      <div className="ab-grid-bg" aria-hidden="true" />

      {/* ── Radial glows ── */}
      <div className="ab-glow ab-glow-1" aria-hidden="true" />
      <div className="ab-glow ab-glow-2" aria-hidden="true" />
      <div className="ab-glow ab-glow-3" aria-hidden="true" />

      {/* ── Floating orbs ── */}
      <div className="ab-orb ab-orb-1" aria-hidden="true" />
      <div className="ab-orb ab-orb-2" aria-hidden="true" />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 1100, margin: "0 auto", padding: "0 24px 100px" }}>

        {/* ── Language switcher ── */}
        {/* <div style={{ display: "flex", justifyContent: isRtl ? "flex-start" : "flex-end", padding: "28px 0 0" }}>
          <div className="ab-lang-bar">
            {LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => switchLang(l.code)}
                className={`ab-lang-btn${activeLang === l.code ? " active" : ""}`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div> */}

        {/* ══ HERO ══ */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          style={{ textAlign: "center", padding: "60px 0 70px" }}
        >
          {/* Badge */}
          
          <div className="ab-badge">
            <img src="/logo 1.png" alt="Dar Oulmes" style={{ width: 140, height: 140, borderRadius: "50%", objectFit: "cover" }} />
          </div>

          <h1 className="ab-hero-title">
            {t("about.header.title")}
          </h1>
          <p className="ab-hero-sub">
            {t("about.header.subtitle")}
          </p>

          {/* Decorative line */}
          <div className="ab-hero-line">
            <span className="ab-hero-line-dot" />
            <span className="ab-hero-line-bar" />
            <span className="ab-hero-line-dot" />
          </div>
        </motion.div>

        {/* ══ VISION ══ */}
        <FuSection icon={<Eye size={20} color="#c8a065" />} title={t("about.vision.title")} delay={0.1}>
          <p className="ab-body-text">{t("about.vision.description")}</p>
        </FuSection>

        {/* ══ WHAT WE OFFER ══ */}
        <FuSection icon={<Layers size={20} color="#c8a065" />} title={t("about.offer.title")} delay={0.15}>
          <div className="ab-offer-grid">
            {["makeup", "kitchen", "smart"].map((key, idx) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                className="ab-offer-card"
              >
                <div className="ab-offer-icon">{OFFER_ICONS[idx]}</div>
                <h3 className="ab-offer-title">{t(`about.offer.${key}.title`)}</h3>
                <p className="ab-offer-desc">{t(`about.offer.${key}.description`)}</p>
              </motion.div>
            ))}
          </div>
        </FuSection>

        {/* ══ EXPERIENCE ══ */}
        <FuSection icon={<Star size={20} color="#c8a065" />} title={t("about.experience.title")} delay={0.1}>
          <p className="ab-body-text">{t("about.experience.description")}</p>
        </FuSection>

        {/* ══ HOW IT WORKS ══ */}
        <FuSection icon={<Zap size={20} color="#c8a065" />} title={t("about.how.title")} delay={0.1}>
          {steps.length === 0 ? (
            <p className="ab-body-text" style={{ fontStyle: "italic", opacity: 0.5 }}>—</p>
          ) : (
            <div className="ab-steps-row">
              {steps.map((step, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.85 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.07, duration: 0.4 }}
                  viewport={{ once: true }}
                  className="ab-step-card"
                >
                  <div className="ab-step-num">{String(idx + 1).padStart(2, "0")}</div>
                  <div className="ab-step-label">{step}</div>
                  {idx < steps.length - 1 && (
                    <div className="ab-step-arrow">
                      <ChevronRight size={14} color="rgba(200,160,101,.4)" />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </FuSection>

        {/* ══ WHY CHOOSE US ══ */}
        <FuSection icon={<Award size={20} color="#c8a065" />} title={t("about.why.title")} delay={0.1}>
          {whyItems.length === 0 ? (
            <p className="ab-body-text" style={{ fontStyle: "italic", opacity: 0.5 }}>—</p>
          ) : (
            <div className="ab-why-grid">
              {whyItems.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: isRtl ? 16 : -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.07 }}
                  viewport={{ once: true }}
                  className="ab-why-card"
                >
                  <span className="ab-why-dot" />
                  <span className="ab-why-text">{item}</span>
                </motion.div>
              ))}
            </div>
          )}
        </FuSection>

        {/* ══ TECH ══ */}
        

        {/* ══ CTA BANNER ══ */}
        <motion.div
          initial={{ opacity: 0, y: 36 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="ab-cta-banner"
        >
          <div className="ab-cta-shimmer" aria-hidden="true" />
          <div className="ab-cta-glow"    aria-hidden="true" />
          <div style={{ position: "relative", zIndex: 1, textAlign: "center" }}>
            <p className="ab-cta-eyebrow">
              <Sparkles size={12} style={{ display: "inline", marginRight: 6 }} />
              Dar Oulmes
            </p>
            <h3 className="ab-cta-title">{t("about.cta.title")}</h3>
            <p className="ab-cta-sub">{t("about.cta.subtitle")}</p>
            <Link to="/products" className="ab-cta-btn">
              {t("about.cta.button")}
              <ArrowRight size={16} />
            </Link>
          </div>
        </motion.div>

      </div>
    </div>
  );
}

/* ── Reusable futuristic section wrapper ── */
function FuSection({ icon, title, children, delay = 0 }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ delay, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="ab-section"
    >
      <div className="ab-section-header">
        <div className="ab-section-icon">{icon}</div>
        <h2 className="ab-section-title">{title}</h2>
        <div className="ab-section-line" />
      </div>
      <div className="ab-section-body">{children}</div>
    </motion.section>
  );
}

/* ── CSS ── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300&family=Montserrat:wght@300;400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  /* ── Animated grid bg ── */
  .ab-grid-bg {
    position: fixed; inset: 0; pointer-events: none; z-index: 0;
    background-image:
      linear-gradient(rgba(200,160,101,.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(200,160,101,.04) 1px, transparent 1px);
    background-size: 52px 52px;
    mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%);
  }

  /* ── Glows ── */
  .ab-glow { position: fixed; pointer-events: none; z-index: 0; border-radius: 50%; filter: blur(80px); }
  .ab-glow-1 { width: 520px; height: 520px; top: -100px; left: -120px; background: radial-gradient(circle, rgba(200,160,101,.12) 0%, transparent 70%); }
  .ab-glow-2 { width: 400px; height: 400px; bottom: 80px; right: -80px; background: radial-gradient(circle, rgba(31,61,58,.35) 0%, transparent 70%); }
  .ab-glow-3 { width: 300px; height: 300px; top: 50%; left: 50%; transform: translate(-50%,-50%); background: radial-gradient(circle, rgba(200,160,101,.06) 0%, transparent 70%); }

  /* ── Orbs ── */
  @keyframes float1 { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-28px) scale(1.04)} }
  @keyframes float2 { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(22px) scale(.97)} }
  .ab-orb { position: fixed; border-radius: 50%; pointer-events: none; z-index: 0; }
  .ab-orb-1 { width:220px; height:220px; top:15%; left:5%; background:rgba(200,160,101,.06); filter:blur(40px); animation:float1 9s ease-in-out infinite; }
  .ab-orb-2 { width:160px; height:160px; bottom:20%; right:8%; background:rgba(31,61,58,.25); filter:blur(30px); animation:float2 12s ease-in-out infinite; }

  /* ── Language bar ── */
  .ab-lang-bar {
    display: inline-flex; gap: 4px;
    background: rgba(255,255,255,.04); border: 1px solid rgba(200,160,101,.18);
    border-radius: 30px; padding: 4px;
  }
  .ab-lang-btn {
    padding: 6px 16px; border-radius: 24px; border: none; cursor: pointer;
    font-family: 'Montserrat',sans-serif; font-size: 11px; font-weight: 700;
    letter-spacing: .12em; transition: all .22s;
    background: transparent; color: rgba(255,255,255,.45);
  }
  .ab-lang-btn.active { background: #c8a065; color: #060d0a; }
  .ab-lang-btn:hover:not(.active) { color: rgba(255,255,255,.8); }

  /* ── Hero ── */
  .ab-badge {
    margin-top:20px;
    display: inline-flex; align-items: center; gap: 8px;
    padding: 7px 18px; border-radius: 30px;
    font-family: 'Montserrat',sans-serif; font-size: 11px; font-weight: 600;
    letter-spacing: .16em; color: rgba(200,160,101,.9); margin-bottom: 28px;
  }
  .ab-hero-title {
    font-family: 'Cormorant Garamond',serif;
    font-size: clamp(46px, 7vw, 88px);
    font-weight: 300; color: #fdf8f2; line-height: 1.1;
    letter-spacing: -.01em; margin-bottom: 20px;
  }
  .ab-hero-sub {
    font-family: 'Montserrat',sans-serif; font-size: 14px; font-weight: 300;
    color: rgba(253,248,242,.5); max-width: 620px; margin: 0 auto 36px;
    line-height: 1.8; letter-spacing: .03em;
  }
  .ab-hero-line { display: flex; align-items: center; justify-content: center; gap: 12px; }
  .ab-hero-line-dot { width: 5px; height: 5px; border-radius: 50%; background: #c8a065; opacity: .6; }
  .ab-hero-line-bar { width: 80px; height: 1px; background: linear-gradient(90deg, transparent, #c8a065, transparent); }

  /* ── Section ── */
  .ab-section {
    margin-bottom: 52px;
    background: rgba(255,255,255,.025);
    border: 1px solid rgba(200,160,101,.1);
    border-radius: 24px; overflow: hidden;
    box-shadow: 0 8px 40px rgba(0,0,0,.3), inset 0 1px 0 rgba(255,255,255,.04);
    backdrop-filter: blur(12px);
  }
  .ab-section-header {
    display: flex; align-items: center; gap: 14px;
    padding: 22px 28px; border-bottom: 1px solid rgba(200,160,101,.1);
    background: rgba(200,160,101,.04);
  }
  .ab-section-icon {
    width: 38px; height: 38px; border-radius: 10px;
    background: rgba(200,160,101,.1); border: 1px solid rgba(200,160,101,.22);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .ab-section-title {
    font-family: 'Cormorant Garamond',serif;
    font-size: 26px; font-weight: 500; color: #fdf8f2; letter-spacing: .01em;
  }
  .ab-section-line { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(200,160,101,.3), transparent); }
  .ab-section-body { padding: 28px; }

  /* ── Body text ── */
  .ab-body-text {
    font-family: 'Montserrat',sans-serif; font-size: 13px; font-weight: 300;
    color: rgba(253,248,242,.6); line-height: 1.9; letter-spacing: .02em;
  }

  /* ── Offer grid ── */
  .ab-offer-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 18px; }
  @media(max-width:700px){ .ab-offer-grid { grid-template-columns: 1fr; } }
  .ab-offer-card {
    position: relative; padding: 26px 22px; border-radius: 16px;
    background: rgba(255,255,255,.03); border: 1px solid rgba(200,160,101,.1);
    transition: border-color .3s, transform .3s, box-shadow .3s;
    overflow: hidden;
  }
  .ab-offer-card::before {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(135deg, rgba(200,160,101,.08) 0%, transparent 60%);
    opacity: 0; transition: opacity .4s;
  }
  .ab-offer-card:hover { border-color: rgba(200,160,101,.35); transform: translateY(-4px); box-shadow: 0 16px 40px rgba(0,0,0,.4); }
  .ab-offer-card:hover::before { opacity: 1; }
  .ab-offer-icon { font-size: 22px; color: #c8a065; margin-bottom: 14px; display: block; }
  .ab-offer-title { font-family:'Cormorant Garamond',serif; font-size:20px; font-weight:500; color:#fdf8f2; margin-bottom:10px; }
  .ab-offer-desc  { font-family:'Montserrat',sans-serif; font-size:12px; font-weight:300; color:rgba(253,248,242,.5); line-height:1.75; }

  /* ── Steps ── */
  .ab-steps-row {
    display: flex; flex-wrap: wrap; gap: 10px; align-items: center;
  }
  .ab-step-card {
    position: relative; flex: 1; min-width: 110px;
    padding: 18px 16px; border-radius: 14px; text-align: center;
    background: rgba(200,160,101,.06); border: 1px solid rgba(200,160,101,.16);
    transition: background .25s, border-color .25s, transform .25s;
  }
  .ab-step-card:hover { background: rgba(200,160,101,.12); border-color: rgba(200,160,101,.35); transform: translateY(-3px); }
  .ab-step-num {
    font-family:'Montserrat',sans-serif; font-size:22px; font-weight:700;
    color:#c8a065; line-height:1; margin-bottom:10px; letter-spacing:-.02em;
  }
  .ab-step-label {
    font-family:'Montserrat',sans-serif; font-size:11px; font-weight:500;
    color:rgba(253,248,242,.75); letter-spacing:.06em; line-height:1.45;
  }
  .ab-step-arrow {
    position: absolute; top: 50%; right: -10px; transform: translateY(-50%);
    z-index: 2; background: #060d0a; border-radius: 50%; padding: 2px;
  }

  /* ── Why grid ── */
  .ab-why-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 12px; }
  @media(max-width:600px){ .ab-why-grid { grid-template-columns: 1fr; } }
  .ab-why-card {
    display: flex; align-items: center; gap: 12px; padding: 16px 18px; border-radius: 12px;
    background: rgba(255,255,255,.03); border: 1px solid rgba(200,160,101,.1);
    transition: background .25s, border-color .25s;
  }
  .ab-why-card:hover { background: rgba(200,160,101,.07); border-color: rgba(200,160,101,.28); }
  .ab-why-dot { width: 6px; height: 6px; border-radius: 50%; background: #c8a065; flex-shrink: 0; }
  .ab-why-text { font-family:'Montserrat',sans-serif; font-size:12px; font-weight:400; color:rgba(253,248,242,.75); }

  /* ── Tech pills ── */
  .ab-tech-pills { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 20px; }
  .ab-tech-pill {
    padding: 6px 14px; border-radius: 20px;
    background: rgba(200,160,101,.08); border: 1px solid rgba(200,160,101,.2);
    font-family:'Montserrat',sans-serif; font-size:11px; font-weight:600;
    color:#c8a065; letter-spacing:.08em;
  }

  /* ── CTA Banner ── */
  @keyframes shimmerSlide { from{transform:translateX(-100%)} to{transform:translateX(100%)} }
  .ab-cta-banner {
    position: relative; border-radius: 24px; overflow: hidden;
    background: linear-gradient(135deg, #0d1f18 0%, #1a3828 40%, #0f2318 70%, #1c1206 100%);
    border: 1px solid rgba(200,160,101,.25);
    padding: 56px 40px;
    box-shadow: 0 30px 80px rgba(0,0,0,.5), 0 0 0 1px rgba(200,160,101,.1) inset;
  }
  .ab-cta-shimmer {
    position: absolute; top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(105deg, transparent 30%, rgba(200,160,101,.07) 50%, transparent 70%);
    animation: shimmerSlide 4s ease-in-out infinite;
  }
  .ab-cta-glow {
    position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%);
    width: 500px; height: 300px; border-radius: 50%;
    background: radial-gradient(ellipse, rgba(200,160,101,.12) 0%, transparent 65%);
    pointer-events: none;
  }
  .ab-cta-eyebrow {
    font-family:'Montserrat',sans-serif; font-size:11px; font-weight:700;
    letter-spacing:.2em; color:#c8a065; margin-bottom:16px;
    display: flex; align-items: center; justify-content: center; gap: 6px;
  }
  .ab-cta-title {
    font-family:'Cormorant Garamond',serif; font-size:clamp(32px,5vw,54px);
    font-weight:400; color:#fdf8f2; margin-bottom:16px; line-height:1.15;
  }
  .ab-cta-sub {
    font-family:'Montserrat',sans-serif; font-size:13px; font-weight:300;
    color:rgba(253,248,242,.55); max-width:480px; margin:0 auto 32px; line-height:1.8;
  }
  .ab-cta-btn {
    display: inline-flex; align-items: center; gap: 10px;
    padding: 14px 32px; border-radius: 40px;
    background: #c8a065; color: #060d0a;
    font-family:'Montserrat',sans-serif; font-size:12px; font-weight:700;
    letter-spacing:.14em; text-decoration:none;
    transition: background .25s, transform .2s, box-shadow .25s;
    box-shadow: 0 8px 28px rgba(200,160,101,.35);
  }
  .ab-cta-btn:hover { background:#d4ae78; transform:translateY(-2px); box-shadow:0 14px 36px rgba(200,160,101,.45); }
`;